import { alpha, type Theme } from '@mui/material';
import {
  DEFAULT_LINEAGE_DISPLAY_SETTINGS,
  LINEAGE_COLOR_BY_OPTIONS,
  LINEAGE_FILTER_HIGHLIGHT,
  LINEAGE_NODE_DETAIL_OPTIONS,
  LINEAGE_SELECTED_SHADOW,
} from '../constants';
import type {
  EnrichedLineageNode,
  LineageColorBy,
  LineageColorCategory,
  LineageDisplaySettings,
  LineageNodeColors,
  LineageNodeDetailField,
  ReactFlowNodeData,
} from '../types';

/** box-shadow value for the selected (clicked) node. */
export const getSelectedShadow = (palette: Theme['palette'], scale = 1) => {
  const { opacity, blur, offsetY } = LINEAGE_SELECTED_SHADOW;
  return `0 ${offsetY * scale}px ${blur * scale}px ${alpha(
    palette.common.black,
    opacity,
  )}`;
};

/** Filter matches: nodes in the filter result, or holding a filtered column. */
export const isFilterMatch = (
  data?: Pick<ReactFlowNodeData, 'isFiltered' | 'isRootNode'>,
) => !!data?.isFiltered || !!data?.isRootNode;

export const getFilterHighlightBackground = (palette: Theme['palette']) =>
  alpha(palette.common.blue4, LINEAGE_FILTER_HIGHLIGHT.opacity);

/** CSS for the ring drawn outside filtered nodes, independent of Color by. */
export const getFilterHighlightCss = (
  palette: Theme['palette'],
  {
    scale = 1,
    innerRingWidth = 0,
  }: {
    scale?: number;
    /** Space in px to leave for another ring drawn around the node, e.g. the selected-path ring. */
    innerRingWidth?: number;
  } = {},
) => {
  const width = LINEAGE_FILTER_HIGHLIGHT.width * scale;
  return `
    outline: ${width}px solid ${getFilterHighlightBackground(palette)};
    outline-offset: ${innerRingWidth}px;
  `;
};

const getAssetTypeColorCategory = (
  metadata?: EnrichedLineageNode,
): LineageColorCategory => {
  if (metadata?.type === 'source') return 'source';
  if (metadata?.type === 'exposure') return 'exposure';
  if (metadata?.type === 'semantic_view') return 'semantic';
  return 'dataAsset';
};

const getTestStatusColorCategory = (
  totals?: Record<string, number>,
): LineageColorCategory => {
  if ((totals?.errors ?? 0) + (totals?.failures ?? 0) > 0) return 'failing';
  if ((totals?.warnings ?? 0) > 0) return 'warning';
  if ((totals?.passed ?? 0) > 0) return 'passing';
  return 'neutral';
};

export const getLineageColorCategory = (
  colorBy: LineageColorBy,
  data?: Pick<ReactFlowNodeData, 'metadata' | 'totals'>,
): LineageColorCategory => {
  if (colorBy === 'assetType') return getAssetTypeColorCategory(data?.metadata);
  if (colorBy === 'testStatus') return getTestStatusColorCategory(data?.totals);
  return 'neutral';
};

const isLineageColorBy = (value: unknown): value is LineageColorBy =>
  LINEAGE_COLOR_BY_OPTIONS.some((option) => option.value === value);

const isLineageNodeDetailField = (
  value: unknown,
): value is LineageNodeDetailField =>
  LINEAGE_NODE_DETAIL_OPTIONS.some((option) => option.value === value);

/**
 * Display settings to render with. Saved values that are no longer valid fall
 * back to the defaults, and coloring by test status requires VIEW_TESTS.
 */
export const getLineageDisplaySettings = (
  saved: Partial<LineageDisplaySettings> | undefined,
  hasTestReadScope: boolean,
): LineageDisplaySettings => {
  const savedColorBy = saved?.colorBy;
  const savedDetailField = saved?.detailField;
  const colorBy = isLineageColorBy(savedColorBy)
    ? savedColorBy
    : DEFAULT_LINEAGE_DISPLAY_SETTINGS.colorBy;
  return {
    colorBy:
      colorBy === 'testStatus' && !hasTestReadScope ? 'assetType' : colorBy,
    nodeMetadata: {
      ...DEFAULT_LINEAGE_DISPLAY_SETTINGS.nodeMetadata,
      ...saved?.nodeMetadata,
    },
    detailField: isLineageNodeDetailField(savedDetailField)
      ? savedDetailField
      : DEFAULT_LINEAGE_DISPLAY_SETTINGS.detailField,
  };
};

/** Text for the card's detail line; asset type is rendered by the node itself. */
export const getLineageNodeDetailText = (
  field: Exclude<LineageNodeDetailField, 'none' | 'assetType'>,
  metadata: EnrichedLineageNode,
): string => {
  switch (field) {
    case 'location':
      return [metadata.databaseName, metadata.schemaName]
        .filter(Boolean)
        .join('.');
    case 'database':
      return metadata.databaseName ?? '';
    case 'schema':
      return metadata.schemaName ?? '';
    case 'materialization':
      return metadata.materialization ?? '';
    case 'owners':
      return metadata.owners?.join(', ') ?? '';
    case 'tags':
      return metadata.tags?.map((tag) => `#${tag}`).join(' ') ?? '';
    case 'path':
      return metadata.path ?? '';
  }
};

export const getLineageCategoryColors = (
  { common }: Theme['palette'],
  category: LineageColorCategory,
): LineageNodeColors => {
  switch (category) {
    case 'source':
      return { border: common.blue2, background: common.lightBlue4 };
    case 'exposure':
      return { border: common.purple, background: common.lightPurple };
    case 'semantic':
      return { border: common.green, background: common.lightTurqoise };
    case 'failing':
      return { border: common.red100, background: common.red10 };
    case 'warning':
      return { border: common.orange100, background: common.orange10 };
    case 'passing':
      return { border: common.green100, background: common.green10 };
    case 'dataAsset':
    case 'neutral':
      return { border: common.gray18, background: common.white };
  }
};
